/**
 * Sidebar item names + the seed permission that gates each one.
 * Source of truth for both navConfig (icons/hrefs) and the role-nav golden
 * test. Ungated items are true self-service (any staff who can open web).
 */
export interface NavItemPermission {
  resource: string;
  /** A single action, or a list of alternatives (any-of). */
  action: string | string[];
}

export interface NavItemAccess {
  name: string;
  permission?: NavItemPermission;
}

export const NAV_ITEMS: NavItemAccess[] = [
  { name: 'Overview', permission: { resource: 'analytics', action: 'school' } },
  { name: 'Approvals' },
  { name: 'My Timetable' },
  { name: 'My Profile' },
  { name: 'Leads Dashboard', permission: { resource: 'crm', action: 'view' } },
  { name: 'Leads', permission: { resource: 'crm', action: 'view' } },
  { name: 'Appointments', permission: { resource: 'crm', action: 'view' } },
  { name: 'Leads Tasks', permission: { resource: 'crm', action: 'view' } },
  { name: 'Reports', permission: { resource: 'crm', action: 'view' } },
  { name: 'WhatsApp (WATI)', permission: { resource: 'crm', action: 'configure' } },
  { name: 'CRM Settings', permission: { resource: 'crm', action: 'configure' } },
  { name: 'Academic Structure', permission: { resource: 'timetable', action: 'view' } },
  { name: 'Academic Calendar', permission: { resource: 'announcements', action: ['view', 'publish'] } },
  { name: 'Exams', permission: { resource: 'exams', action: 'schedule' } },
  { name: 'Lesson Plans', permission: { resource: 'lesson_plans', action: ['submit', 'approve'] } },
  { name: 'Homework', permission: { resource: 'homework', action: ['assign', 'view'] } },
  { name: 'Diary', permission: { resource: 'diary', action: ['write', 'view'] } },
  { name: 'Students', permission: { resource: 'students', action: 'view' } },
  { name: 'Teachers', permission: { resource: 'teachers', action: 'view' } },
  { name: 'Student Roster', permission: { resource: 'students', action: 'manage' } },
  { name: 'Admissions', permission: { resource: 'admissions', action: 'manage' } },
  { name: 'Year-End Promotion', permission: { resource: 'students', action: 'manage' } },
  { name: 'Student Leave', permission: { resource: 'attendance', action: 'mark' } },
  { name: 'Student Attendance', permission: { resource: 'attendance', action: 'view_school' } },
  { name: 'Staff Attendance', permission: { resource: 'attendance', action: 'view_school' } },
  { name: 'Regularizations' },
  { name: 'Leave Management' },
  { name: 'Geofence Settings', permission: { resource: 'hr', action: 'manage' } },
  { name: 'Fees', permission: { resource: 'fees', action: ['structure_manage', 'collect', 'view_reports'] } },
  { name: 'Inventory', permission: { resource: 'inventory', action: 'manage' } },
  { name: 'Library', permission: { resource: 'library', action: 'manage' } },
  { name: 'Gate', permission: { resource: 'visitors', action: 'manage' } },
  { name: 'Expenses', permission: { resource: 'expenses', action: ['record', 'approve', 'budget_manage', 'claim'] } },
  { name: 'Tasks' },
  { name: 'Fleet Live View', permission: { resource: 'transport', action: 'manage' } },
  { name: 'Transport Setup', permission: { resource: 'transport', action: 'manage' } },
  { name: 'PR Tickets', permission: { resource: 'tickets', action: ['triage', 'manage'] } },
  { name: 'PTM', permission: { resource: 'ptm', action: 'manage' } },
  { name: 'Announcements', permission: { resource: 'announcements', action: 'publish' } },
  { name: 'Gallery', permission: { resource: 'gallery', action: 'upload' } },
  { name: 'HR & Payroll', permission: { resource: 'hr', action: 'manage' } },
  { name: 'Users & Roles', permission: { resource: 'users', action: 'manage' } },
  { name: 'Team / Org Chart', permission: { resource: 'settings', action: 'manage' } },
  { name: 'Org & School Settings', permission: { resource: 'settings', action: 'manage' } },
];

export function navPermission(name: string): NavItemPermission | undefined {
  const item = NAV_ITEMS.find((entry) => entry.name === name);
  if (!item) {
    throw new Error(`navPermission: unknown sidebar item "${name}"`);
  }
  return item.permission;
}
